import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { ChecklistItem } from 'shared/types';

const ChecklistEditor: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { current_user, current_family, updateTask } = useAppContext();
  
  const [newItemText, setNewItemText] = useState('');
  const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);
  const [editingText, setEditingText] = useState('');

  if (!current_family || !current_user || !(current_user as any).is_admin) {
    return <div>Access denied. Admin privileges required.</div>;
  }

  const task = current_family.tasks.find((t: any) => t.id === taskId);

  if (!task) {
    return <div>Task not found.</div>;
  }

  const handleAddItem = async () => {
    if (!newItemText.trim()) return;

    const newItem: ChecklistItem = {
      id: Date.now().toString(),
      text: newItemText.trim(),
      images: []
    };

    const updatedChecklist = [...task.checklist, newItem];
    
    try {
      await updateTask(taskId!, { checklist: updatedChecklist } as any);
      setNewItemText('');
    } catch (error) {
      console.error('Failed to add checklist item:', error);
    }
  };

  const handleEditItem = (item: ChecklistItem) => {
    setEditingItem(item);
    setEditingText(item.text);
  };

  const handleSaveEdit = async () => {
    if (!editingItem || !editingText.trim()) return;

    const updatedChecklist = task.checklist.map(item =>
      item.id === editingItem.id ? { ...item, text: editingText.trim() } : item
    );

    try {
      await updateTask(taskId!, { checklist: updatedChecklist } as any);
      setEditingItem(null);
      setEditingText('');
    } catch (error) {
      console.error('Failed to update checklist item:', error);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    const updatedChecklist = task.checklist.filter(item => item.id !== itemId);
    
    try {
      await updateTask(taskId!, { checklist: updatedChecklist } as any);
    } catch (error) {
      console.error('Failed to delete checklist item:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditingText('');
  };

  const handleBack = () => {
    navigate('/admin');
  };

  return (
    <div className="max-w-full">
      <div className="flex items-center gap-4 mb-8 pb-4 border-b-2 border-gray-200">
        <button onClick={handleBack} className="text-blue-600 hover:text-blue-800 font-medium">
          ← Back to Admin Panel
        </button>
        <h2 className="text-3xl font-bold text-gray-800 m-0">Edit Checklist: {task.title}</h2>
      </div>

      <div className="flex flex-col gap-8">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Add New Checklist Item</h3>
          <div className="flex gap-4 items-center flex-wrap">
            <input
              type="text"
              value={newItemText}
              onChange={(e) => setNewItemText(e.target.value)}
              placeholder="Enter checklist item text..."
              className="flex-1 min-w-[300px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button onClick={handleAddItem} className="btn btn-primary">
              Add Item
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Current Checklist Items</h3>
          {task.checklist.length === 0 ? (
            <p className="text-center text-gray-400 italic py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              No checklist items yet. Add some above!
            </p>
          ) : (
            <div className="space-y-6">
              {task.checklist.map((item, index) => (
                <div key={item.id} className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                  {editingItem?.id === item.id ? (
                    <div className="space-y-4">
                      <input
                        type="text"
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <div className="flex gap-2">
                        <button onClick={handleCancelEdit} className="btn btn-cancel btn-small">
                          Cancel
                        </button>
                        <button onClick={handleSaveEdit} className="btn btn-primary btn-small">
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4 flex-1">
                        <span className="text-lg font-medium text-gray-800">{index + 1}.</span>
                        <span className="text-lg font-medium text-gray-800">{item.text}</span>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleEditItem(item)}
                          className="btn btn-secondary btn-small"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteItem(item.id)}
                          className="btn btn-danger btn-small"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChecklistEditor;
